import { createContext, FC, ReactNode, useContext } from 'react'
import { TableNoteConfig, ICellNoteIndicator } from '@common/interfaces/notes'
import { useCellNotes } from '../../../hooks/useCellNotes'

interface CellNotesContextType {
  indicators: ICellNoteIndicator[]
  indicatorsByCell: Record<string, ICellNoteIndicator>
  isLoading: boolean
  cellHasNotes: (rowId: string, columnId: string, recordDate?: string | null) => boolean
  getCellNoteCount: (rowId: string, columnId: string, recordDate?: string | null) => number
  getCellNoteIndicator: (
    rowId: string,
    columnId: string,
    recordDate?: string | null,
  ) => ICellNoteIndicator | null
  getCellAttribution: (
    rowId: string,
    columnId: string,
    recordDate?: string | null,
  ) => {
    suggestedCount: number
    establishedCount: number
    establishedNoteCount: number
    confirmedLinkCount: number
  }
  createNote: (
    cellCoords: {
      rowId: string
      columnId: string
      value: string
      recordDate?: string | null
    },
    noteData: Record<string, unknown>,
  ) => unknown
  config: TableNoteConfig
}

const CellNotesContext = createContext<CellNotesContextType | null>(null)

interface CellNotesProviderProps {
  tableNoteConfig: TableNoteConfig
  children: ReactNode
}

export const CellNotesProvider: FC<CellNotesProviderProps> = ({ tableNoteConfig, children }) => {
  const cellNotesHook = useCellNotes(tableNoteConfig)

  // Not memoised, deliberately: `useCellNotes` returns a fresh object literal on every
  // render, so a `useMemo` keyed on it would re-run every time anyway — it would read as
  // a fix while doing nothing. Making this stable means memoising the hook's own return
  // (and the `tableNoteConfig` object each of the ~20 call sites passes), which is
  // desktop-performance work with a much wider blast radius than this branch. On mobile
  // the cost is gone regardless: `TableCell` no longer mounts a consumer per cell.
  const contextValue: CellNotesContextType = {
    ...cellNotesHook,
    config: tableNoteConfig,
  }

  return <CellNotesContext.Provider value={contextValue}>{children}</CellNotesContext.Provider>
}

// Custom hook to use cell notes context
export const useCellNotesContext = (): CellNotesContextType => {
  const context = useContext(CellNotesContext)
  if (!context) {
    throw new Error('useCellNotesContext must be used within a CellNotesProvider')
  }
  return context
}

export default CellNotesProvider
